/**
 * Client-side mirror of `apps.proposal.contract_validation.validate_contract`
 * (`ezbuild_back`) — pure, synchronous, no network call — so the Contract
 * Validation card can recalculate live while a user edits the form, before
 * anything is saved. Every rule here is a deliberate copy of the backend's
 * own, not an independent guess:
 *
 * - Milestones are read across EVERY Project Type/Scope Group unconditionally
 *   (the form's whole `phases` array — there is no "selected tab" concept).
 * - A milestone's duration is normalized to days via `{days: 1, weeks: 7,
 *   months: 30}`. Deliberately excludes "years" (offered as a duration-unit
 *   option on the milestone row) — a milestone in years has no reliable
 *   day-count answer today, so it reads as `insufficient_data` on the WHOLE
 *   duration check, exactly like the backend, not a client-side improvement.
 * - Milestone durations are simply summed (Phase 1: sequential/
 *   non-overlapping) — there is no scheduling/dependency model for a
 *   Contract's own milestones, only for a Lead's.
 * - `insufficient_data` beats `warning`/`valid` whenever a figure cannot be
 *   computed at all (no milestones, no Sales Price, no dates, or even one
 *   milestone missing a usable duration) — never silently reads as 0.
 *
 * Keep this in lockstep with `contract_validation.py` if either changes.
 */

import dayjs, { type Dayjs } from "dayjs"

import type {
  ContractValidationFormValues,
  ContractValidationPhaseInput,
  ContractValidationResult,
  ContractValidationStatus,
  DurationValidation,
  SalesPriceValidation
} from "./types.ts"

const VALID: ContractValidationStatus = "valid"
const WARNING: ContractValidationStatus = "warning"
const INSUFFICIENT_DATA: ContractValidationStatus = "insufficient_data"

// Same three-unit vocabulary as the backend's `_DAYS_PER_UNIT` — "years" is
// intentionally absent, see module docstring.
const DAYS_PER_UNIT: Record<string, number> = { days: 1, weeks: 7, months: 30 }

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null
  const n = typeof value === "number" ? value : parseFloat(String(value))
  return Number.isFinite(n) ? n : null
}

// Rounds to cents before an equality check, so float arithmetic on money
// (e.g. 0.1 + 0.2) can never read as a spurious warning.
function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

function parseDateLike(value: unknown): Dayjs | null {
  if (!value) return null
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null
  if (value instanceof Date) {
    const d = dayjs(value)
    return d.isValid() ? d : null
  }
  if (typeof value === "string") {
    const iso = dayjs(value, "YYYY-MM-DD", true)
    if (iso.isValid()) return iso
    const generic = dayjs(value)
    return generic.isValid() ? generic : null
  }
  return null
}

/** One milestone's own duration, normalized to days — `null` when it cannot
 * be (no `duration_days` at all, or a unit outside the three-unit
 * vocabulary). `null` always means "unusable for a sum", never "zero". */
function normalizeDurationDays(
  durationDays: unknown,
  durationType: unknown
): number | null {
  const days = toNumber(durationDays)
  if (days === null) return null
  const unit = String(durationType || "days")
    .trim()
    .toLowerCase()
  const multiplier = DAYS_PER_UNIT[unit]
  if (multiplier === undefined) return null
  return days * multiplier
}

/** SUM of every milestone's own price, across every Project Type/Scope
 * Group. `null` only when there are no milestones at all. A milestone's own
 * price defaults to 0 when blank/unparseable — mirroring `ProposalPhase.
 * budget`'s DB-level `default=0` (a saved milestone never truly lacks a
 * price; a momentarily-blank input while typing reads the same way). */
function milestoneTotalPrice(
  phases: ContractValidationPhaseInput[] | null | undefined
): number | null {
  if (!phases || phases.length === 0) return null
  return phases.reduce((sum, phase) => sum + (toNumber(phase.budget) ?? 0), 0)
}

/** SUM of every milestone's own normalized duration. `null` when there are
 * no milestones, OR when even one milestone is missing a usable duration —
 * mirrors `duration_days`'s genuine DB nullability (no default), unlike
 * price above. */
function milestoneTotalDurationDays(
  phases: ContractValidationPhaseInput[] | null | undefined
): number | null {
  if (!phases || phases.length === 0) return null
  let total = 0
  for (const phase of phases) {
    const days = normalizeDurationDays(phase.duration_days, phase.duration_type)
    if (days === null) return null
    total += days
  }
  return total
}

function validateSalesPrice(
  salesPriceRaw: unknown,
  phases: ContractValidationPhaseInput[] | null | undefined
): SalesPriceValidation {
  const salesPrice = toNumber(salesPriceRaw)
  const milestoneTotal = milestoneTotalPrice(phases)

  if (salesPrice === null || milestoneTotal === null) {
    return {
      status: INSUFFICIENT_DATA,
      sales_price: salesPrice,
      milestone_total: milestoneTotal,
      difference: null
    }
  }

  const difference = round2(salesPrice - milestoneTotal)
  return {
    status: difference === 0 ? VALID : WARNING,
    sales_price: round2(salesPrice),
    milestone_total: round2(milestoneTotal),
    difference
  }
}

function validateDuration(
  startDateRaw: unknown,
  endDateRaw: unknown,
  phases: ContractValidationPhaseInput[] | null | undefined
): DurationValidation {
  const start = parseDateLike(startDateRaw)
  const end = parseDateLike(endDateRaw)
  const contractDurationDays =
    start && end ? end.startOf("day").diff(start.startOf("day"), "day") : null
  const milestoneDurationDays = milestoneTotalDurationDays(phases)

  if (contractDurationDays === null || milestoneDurationDays === null) {
    return {
      status: INSUFFICIENT_DATA,
      contract_duration_days: contractDurationDays,
      milestone_duration_days: milestoneDurationDays,
      difference_days: null
    }
  }

  const differenceDays = contractDurationDays - milestoneDurationDays
  return {
    status: differenceDays === 0 ? VALID : WARNING,
    contract_duration_days: contractDurationDays,
    milestone_duration_days: milestoneDurationDays,
    difference_days: differenceDays
  }
}

/** The full Sales Price + Contract Duration self-consistency check for the
 * Contract currently in the form — reads ONLY the form's own current
 * values, never the Lead or a linked Proposal. */
function calculateContractValidation(
  values: ContractValidationFormValues
): ContractValidationResult {
  return {
    sales_price: validateSalesPrice(values.sales_price, values.phases),
    duration: validateDuration(
      values.start_date,
      values.completion_date,
      values.phases
    )
  }
}

/** Whether either check is in `warning` state — the single predicate both
 * the live card and the Generate-time gate use to decide "show something",
 * so they can never disagree about what counts as a mismatch. */
function contractValidationHasWarnings(result: ContractValidationResult): boolean {
  return result.sales_price.status === WARNING || result.duration.status === WARNING
}

export {
  calculateContractValidation,
  contractValidationHasWarnings,
  milestoneTotalPrice,
  milestoneTotalDurationDays,
  normalizeDurationDays
}
